import { Component, ViewChild, AfterViewInit } from '@angular/core';
import * as moment from 'moment';
import { InfoPopupComponent } from '../info-popup/info-popup.component';
import { UserService } from '../../../shared/services/user.service';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Achievement, AchievementService } from 'app/shared/services/achievement.service';
import { HelpDialogComponent } from '../achievement/components/help-dialog/help-dialog.component';
import { MatDialog } from '@angular/material';
import { TranslateService } from '@ngx-translate/core';

enum AchievementLevel {
  UNLOCKED = 'unlocked',
  GREEN = 'green',
  BRONZE = 'bronze',
  SILBER = 'silber',
  GOLD = 'gold',
}

@Component({
  selector: 'app-achievement',
  styleUrls: ['achievement.component.scss'],
  templateUrl: 'achievement.component.html',
})
export class AchievementComponent implements AfterViewInit {
  date: any;
  achievementData: Achievement[];
  achievementChunks: Achievement[][];

  aliasForm = new FormGroup({
    alias: new FormControl('', [Validators.maxLength(30)]),
  });

  @ViewChild(InfoPopupComponent) aliasPopup: InfoPopupComponent;

  constructor(
    private userService: UserService,
    private achievementService: AchievementService,
    private dialog: MatDialog,
    private translateService: TranslateService
  ) {
    this.date = moment();
    this.loadAllAchievements();
  }
  
  /**
   * Translates achievement name using the translation service
   * @param achievementName The original achievement name
   * @returns The translated achievement name
   */
  translateAchievementName(achievementName: string): string {
    // If achievement name is missing or empty, return empty string
    if (!achievementName) {
      return '';
    }
    
    const translationKey = `ACHIEVEMENTS.achievementNames.${achievementName}`;
    const translation = this.translateService.instant(translationKey);
    
    // Return original name if translation is not found (returns the key)
    return translation === translationKey ? achievementName : translation;
  }
  
  /**
   * Translates achievement description using the translation service
   * @param achievementName The original achievement name
   * @returns The translated achievement description
   */
  translateAchievementDescription(achievementName: string): string {
    // If achievement name is missing or empty, return empty string
    if (!achievementName) {
      return '';
    }
    
    const translationKey = `ACHIEVEMENTS.achievementDescriptions.${achievementName}`;
    const translation = this.translateService.instant(translationKey);
    
    // If no translation is found, fall back to original description
    if (translation === translationKey) {
      // Find the achievement in our data
      const achievement = this.achievementData && this.achievementData.find(a => a.name === achievementName);
      return (achievement && achievement.description) ? achievement.description : '';
    }
    
    return translation;
  }

  ngAfterViewInit() {
    this.userService.currentUser.subscribe((user) => {
      if (!user) {
        return;
      }
    });
  }

  async loadAllAchievements() {
    let data = await this.achievementService.getAllAchievementsForUser();
    this.achievementData = data;
    this.achievementChunks = this.achievementService.chunkArray(this.achievementData, 1);
  }

  async loadUnlockedAchievements() {
    let data = await this.achievementService.getAllAchievementsForUser();

    const unlockedAchievements = data.filter((achievement) =>
      achievement.hasLevels ? achievement.progress >= achievement.levels.green : achievement.progress === 1
    );

    this.achievementData = unlockedAchievements;
    this.achievementChunks = this.achievementService.chunkArray(this.achievementData, 1);
  }

  async loadLockedAchivements() {
    let data = await this.achievementService.getAllAchievementsForUser();

    const lockedAchievements = data.filter((achievement) =>
      achievement.hasLevels ? achievement.progress < achievement.levels.green : achievement.progress === 0
    );

    this.achievementData = lockedAchievements;
    this.achievementChunks = this.achievementService.chunkArray(this.achievementData, 1);
  }

  getNextAchivementLevel(achievement: Achievement): string {
    const currentLevel = this.getCurrentAchievementLevel(achievement);
    const levelsArr = [
      AchievementLevel.UNLOCKED,
      AchievementLevel.GREEN,
      AchievementLevel.BRONZE,
      AchievementLevel.SILBER,
      AchievementLevel.GOLD,
    ];

    const currentIndex = levelsArr.indexOf(currentLevel);
    if (currentIndex === levelsArr.length - 1) {
      return AchievementLevel.GOLD;
    } else {
      return levelsArr[currentIndex + 1];
    }
  }

  getCurrentAchievementLevel(achievement: Achievement): AchievementLevel {
    const progress = achievement.progress;
    const levels = achievement.levels;
    const levelsArr = [AchievementLevel.GREEN, AchievementLevel.BRONZE, AchievementLevel.SILBER, AchievementLevel.GOLD];

    if (levels) {
      let progressToNextLevel = progress;
      let currentLevel = AchievementLevel.UNLOCKED;

      for (const levelName of levelsArr) {
        const level = levels[levelName];
        if (progressToNextLevel >= level) {
          currentLevel = levelName;
          progressToNextLevel -= level;
        } else {
          break; // Stop checking once the current level is found
        }
      }
      return currentLevel;
    } else if (achievement.finishedAt || (!achievement.hasLevels && achievement.progress === 1)) {
      return AchievementLevel.GOLD;
    } else {
      return AchievementLevel.UNLOCKED;
    }
  }

  getProgressString(achievement: Achievement): string {
    if (achievement.hasLevels) {
      const currentLevel = this.getCurrentAchievementLevel(achievement);
      const currentProgress = this.getCurrentAchievementProgress(achievement);

      switch (currentLevel) {
        case AchievementLevel.UNLOCKED:
          return `${currentProgress}/${achievement.levels.green}`;

        case AchievementLevel.GREEN:
          return `${currentProgress}/${achievement.levels.bronze}`;

        case AchievementLevel.BRONZE:
          return `${currentProgress}/${achievement.levels.silber}`;

        case AchievementLevel.SILBER:
          return `${currentProgress}/${achievement.levels.gold}`;

        case AchievementLevel.GOLD:
          return '';
      }
    } else {
      return '';
    }
  }

  getProgressColor(achievement: Achievement): string {
    if (achievement.hasLevels) {
      const currentLevel = this.getCurrentAchievementLevel(achievement);

      switch (currentLevel) {
        case AchievementLevel.UNLOCKED:
          return '#929292';

        case AchievementLevel.GREEN:
          return '#1F8D16';

        case AchievementLevel.BRONZE:
          return '#8F551B';

        case AchievementLevel.SILBER:
          return '#646464';
      }
    }
  }

  getCurrentAchievementProgress(achievement: Achievement): number {
    const currentLevel = this.getCurrentAchievementLevel(achievement);
    const levels = achievement.levels;
    let currentProgress = achievement.progress;

    switch (currentLevel) {
      case AchievementLevel.UNLOCKED:
        return currentProgress;

      case AchievementLevel.GREEN:
        return currentProgress - levels[AchievementLevel.GREEN];

      case AchievementLevel.BRONZE:
        return currentProgress - levels[AchievementLevel.GREEN] - levels[AchievementLevel.BRONZE];

      case AchievementLevel.SILBER:
        return (
          currentProgress -
          levels[AchievementLevel.GREEN] -
          levels[AchievementLevel.BRONZE] -
          levels[AchievementLevel.SILBER]
        );

      case AchievementLevel.GOLD:
        return (
          currentProgress -
          levels[AchievementLevel.GREEN] -
          levels[AchievementLevel.BRONZE] -
          levels[AchievementLevel.SILBER] -
          levels[AchievementLevel.GOLD]
        );
    }
  }

  getProgressPercentage(achievement: Achievement) {
    if (!achievement.hasLevels) {
      return 0;
    }

    const progress = this.getCurrentAchievementProgress(achievement);
    const nextLevel = this.getNextAchivementLevel(achievement);
    const limit = achievement.levels[nextLevel];
    const progressPercentage = Math.floor((progress * 100) / limit);

    return progressPercentage;
  }

  showHelpPage() {
    const dialogRef = this.dialog.open(HelpDialogComponent, {
      maxWidth: '800px',
      maxHeight: '600px',
      width: '80%',
      height: '100%',
    });
  }

  toggleAll() {
    // fetch all achivements
    this.loadAllAchievements();
  }

  toggleLocked() {
    // fetch open achivements
    this.loadLockedAchivements();
  }

  toggleUnlocked() {
    // fetch unlocked achivements
    this.loadUnlockedAchievements();
  }
}



// WEBPACK FOOTER //
// /codebuild/output/src1521087412/src/client/web-client/src/app/theme/components/achievement/achievement.component.ts